import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { SyncTypeEnum } from 'src/app/shared/projects/projects.model';
import { CategoryResponse, TAllocationTerritories, TerritoryResponse } from './dictionary.response';
import { SyncApiService } from 'src/app/shared/services/sync-api.service';
import { ICurrency } from '../types/currency';
import { shareReplay } from 'rxjs/operators';
import {
  TGenreDTO,
  TLanguageDTO,
  TMoodDTO,
  TThemeDTO,
  TVersionDTO,
  TVocalMixDTO,
} from 'src/app/asset-management/types';
import { TCompany } from '../types/company';

const SPECIAL_CASES = {
  _124: [
    {
      code: '2902',
      id: 2902,
      footer: '',
      header: '',
      name: 'Warner Chappell Music Canada Ltd.',
    },
  ],
};

@Injectable({
  providedIn: 'root',
})
export class DictionaryService {
  private readonly cache: Map<string, Observable<any>> = new Map();

  constructor(public api: SyncApiService) {}

  fetchCompaniesByTerritory(territoryId: number, projectSyncType?: SyncTypeEnum): Observable<TCompany[]> {
    const syncType = projectSyncType?.charAt(0) === 'G' ? projectSyncType.slice(1) : projectSyncType;

    return !SPECIAL_CASES[territoryId]
      ? this.api.get('suggestion/companies', { territoryId, syncType })
      : of(SPECIAL_CASES[territoryId]);
  }

  fetchInvoiceTypes(territoryCode: string): Observable<string[]> {
    return this.api.get('invoice-rechnung', { territoryCode });
  }

  fetchExerciseConditions(): Observable<string[]> {
    return this.cached('exercise-conditions');
  }

  fetchTerritories(): Observable<TerritoryResponse> {
    return this.cached('territories');
  }

  fetchCategories(syncType: SyncTypeEnum = SyncTypeEnum.PSR, territoryCode?: string): Observable<CategoryResponse> {
    const params: any = {};

    if (syncType) {
      params.syncType = syncType;
    }

    if (territoryCode) {
      params.territoryCode = territoryCode;
    }

    return this.api.get('sync-categories', params);
  }

  fetchCurrencies(): Observable<ICurrency[]> {
    return this.cached('currencies');
  }

  fetchMedia(syncType: SyncTypeEnum = SyncTypeEnum.MSR, syncCategory?: any): Observable<string[]> {
    const params: any = { syncType };

    if (syncCategory) {
      params.syncCategory = syncCategory;
    }

    return this.api.get('media-types', params);
  }

  fetchMFN(syncType: SyncTypeEnum): Observable<string[]> {
    return this.api.get('mfn-types', { syncType });
  }

  fetchRoyaltyBearingBasis(): Observable<string[]> {
    return this.api.get('royalty-bearing-basis');
  }

  fetchRoyaltyBearingAdditionalParameters(): Observable<string[]> {
    return this.api.get('royalty-bearing-additional-parameters');
  }

  fetchGenres(): Observable<TGenreDTO[]> {
    return this.cached('genres');
  }

  fetchMoods(): Observable<TMoodDTO[]> {
    return this.cached('moods');
  }

  fetchThemes(): Observable<TThemeDTO[]> {
    return this.cached('themes');
  }

  fetchVersions(): Observable<TVersionDTO[]> {
    return this.cached('versions');
  }

  fetchVocalMixes(): Observable<TVocalMixDTO[]> {
    return this.cached('vocal-mix');
  }

  fetchLanguages(): Observable<TLanguageDTO[]> {
    return this.cached('languages');
  }

  // fetchInvoiceTypes(): Observable<string[]> {
  //   return this.cached('invoice-rechnung');
  // }

  fetchCompanies(): Observable<TCompany[]> {
    return this.cached('companies');
  }

  fetchAllocationTerritories(): Observable<TAllocationTerritories> {
    return this.cached('allocation-territories');
  }

  fetchActivities(): Observable<string[]> {
    return this.cached('tracked-activities');
  }

  fetchActivitiesCLA(claId: number): Observable<string[]> {
    return this.api.get(`cla/${claId}/events/types`);
  }

  private cached<T>(key: string): Observable<T> {
    const data$: Observable<T> = this.cache.has(key) ? this.cache.get(key) : this.api.get<T>(key).pipe(shareReplay(1));

    this.cache.set(key, data$);

    return data$;
  }
}
